import React, { useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { CollectionEmailTemplate, TONE_LABELS } from '../../config/model';
import { CollectionEmailSendStatus, CollectionEmailSendSource, STATUS_LABELS, SOURCE_LABELS } from '../model';
import { EmailSendsQueryParams } from '../hooks/useCollectionEmailSends';

export type FilterFields = Omit<EmailSendsQueryParams, 'location' | 'page' | 'per_page'>;

interface Props {
  onChange: (filters: FilterFields) => void;
}

const TONES: CollectionEmailTemplate['tone'][] = ['friendly', 'second_notice', 'urgent'];
const STATUSES: CollectionEmailSendStatus[] = ['queued', 'sent', 'failed'];
const SOURCES: CollectionEmailSendSource[] = ['automatic', 'manual'];

const EMPTY_FILTERS: FilterFields = {
  status: '',
  tone: '',
  send_source: '',
  company: '',
  bill_code: '',
};

const EmailHistoryFilters: React.FC<Props> = ({ onChange }) => {
  const [fields, setFields] = useState<FilterFields>(EMPTY_FILTERS);

  const handleChange = (key: keyof FilterFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onChange(fields);
  };

  const handleClear = () => {
    setFields(EMPTY_FILTERS);
    onChange(EMPTY_FILTERS);
  };

  return (
    <Box sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={fields.status ?? ''} label="Status" onChange={(e) => handleChange('status', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Tone</InputLabel>
            <Select value={fields.tone ?? ''} label="Tone" onChange={(e) => handleChange('tone', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {TONES.map((t) => (
                <MenuItem key={t} value={t}>
                  {TONE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select
              value={fields.send_source ?? ''}
              label="Source"
              onChange={(e) => handleChange('send_source', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {SOURCES.map((s) => (
                <MenuItem key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Company"
            value={fields.company ?? ''}
            onChange={(e) => handleChange('company', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Bill Code"
            value={fields.bill_code ?? ''}
            onChange={(e) => handleChange('bill_code', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="outlined" size="small" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailHistoryFilters;
